/** Settings page declarations use the 390x844 canvas, including the 24dp top inset. */
import type { ComponentMeta } from '../../core/types';
import { buildComponentMeta } from '../../ui/meta';
import type { BoxLayout, ComponentMetaInput } from '../../ui/meta';

export const PAGE_TOP = 24;
export const SETTINGS_ENTRY_LINKS = [
  { id: 'account', label: '账户与余额', path: '/account' },
  { id: 'marketplace', label: '栀歆模型广场', path: '/marketplace' },
  { id: 'billing', label: '充值中心', path: '/billing' },
  { id: 'providers', label: '自定义提供商', path: '/providers' },
  { id: 'memory', label: '记忆管理', path: '/memory' },
  { id: 'gallery', label: '图库', path: '/gallery' },
  { id: 'tts', label: '语音与 TTS', path: '/tts' },
] as const;
const ENTRIES_TOP = 332;
const ENTRIES_HEIGHT = 52 + SETTINGS_ENTRY_LINKS.length * 52;
const BACKUP_TOP = ENTRIES_TOP + ENTRIES_HEIGHT + 16;
function meta(
  id: string,
  label: string,
  role: ComponentMetaInput['role'],
  x: number,
  y: number,
  width: number,
  height: number,
  zIndex = 3,
): ComponentMetaInput {
  return {
    id: 'page.settings.' + id,
    page: 'settings',
    label,
    role,
    layout: { x, y, width, height },
    zIndex,
    notes: role === 'modal' || zIndex >= 100
      ? '覆盖设置页的确认弹窗；遮罩拦截触摸，按钮支持键盘焦点与返回关闭。'
      : '设置页纵向滚动内容；账户区按登录状态显示，入口保持至少 44dp 触摸高度。',
  };
}

/** Account header replaces the former model parameter controls. */
export const SETTINGS_PANEL_COMPONENTS: ComponentMetaInput[] = [
  meta('root', '设置页面容器', 'container', 0, 24, 390, 744, 2),
  meta('title', '设置页面标题', 'text', 16, 24, 358, 32),
  meta('auth.login', '登录', 'button', 24, 80, 161, 44),
  meta('auth.register', '注册', 'button', 205, 80, 161, 44),
  meta('account.card', '当前账户', 'container', 12, 72, 366, 84, 1),
  meta('account.email', '当前账户邮箱', 'text', 24, 84, 342, 24),
  meta('account.balance', '当前账户余额', 'text', 24, 116, 342, 28),
  meta('account.profile', '个人资料', 'button', 12, 168, 366, 44),
  meta('account.error', '账户加载错误', 'text', 24, 128, 342, 28),
  meta('subscription.entry', '官方订阅', 'button', 12, 228, 366, 88),
  meta('subscription.label', '官方订阅', 'text', 24, 240, 156, 24, 4),
  meta('subscription.value', '当前订阅状态', 'text', 184, 240, 182, 24, 4),
  meta('subscription.hint', '订阅模式说明', 'text', 24, 272, 342, 32, 4),
];

export const SETTINGS_BACKUP_COMPONENTS: ComponentMetaInput[] = [
  meta('backup.card', '数据备份 / 恢复卡片', 'container', 12, BACKUP_TOP, 366, 252, 1),
  meta('backup.title', '数据备份 / 恢复', 'text', 24, BACKUP_TOP + 12, 342, 24),
  meta('backup.hint', '备份说明', 'text', 24, BACKUP_TOP + 42, 342, 36),
  meta('backup.exportButton', '导出加密备份', 'button', 24, BACKUP_TOP + 84, 342, 44),
  meta('backup.importInput', '选择备份文件并还原', 'input', 24, BACKUP_TOP + 136, 342, 44),
  meta('backup.status', '恢复结果', 'text', 24, BACKUP_TOP + 192, 342, 48),
  meta('backup.error', '备份错误提示', 'text', 24, BACKUP_TOP + 192, 342, 48),
  meta('backup.confirm.backdrop', '恢复确认遮罩', 'background', 0, 24, 390, 744, 100),
  meta('backup.confirm.dialog', '恢复确认弹窗', 'modal', 24, 310, 342, 220, 101),
  meta('backup.confirm.title', '恢复确认标题', 'text', 40, 326, 310, 28, 102),
  meta('backup.confirm.text', '恢复确认说明', 'text', 40, 362, 310, 72, 102),
  meta('backup.confirm.cancel', '取消恢复', 'button', 40, 462, 144, 44, 102),
  meta('backup.confirm.confirm', '确认恢复', 'button', 206, 462, 144, 44, 102),
  meta('backup.dialog.backdrop', '加密备份遮罩', 'background', 0, 24, 390, 744, 100),
  meta('backup.dialog', '加密备份弹窗', 'modal', 16, 80, 358, 560, 101),
  meta('backup.dialog.title', '加密备份标题', 'text', 32, 96, 326, 28, 102),
  meta('backup.dialog.hint', '加密备份说明', 'text', 32, 128, 326, 36, 102),
  meta('backup.dialog.passwordLabel', '备份密码标签', 'text', 32, 190, 326, 20, 102),
  meta('backup.dialog.passwordInput', '备份密码输入框', 'input', 32, 214, 326, 44, 102),
  meta('backup.dialog.confirmLabel', '确认密码标签', 'text', 32, 270, 326, 20, 102),
  meta('backup.dialog.confirmInput', '确认密码输入框', 'input', 32, 294, 326, 44, 102),
  meta('backup.dialog.summary', '备份摘要', 'text', 32, 172, 326, 96, 102),
  meta('backup.dialog.modeLabel', '恢复模式标签', 'text', 32, 280, 326, 20, 102),
  meta('backup.dialog.modeMerge', '合并恢复', 'button', 32, 304, 158, 44, 102),
  meta('backup.dialog.modeReplace', '完整替换', 'button', 200, 304, 158, 44, 102),
  meta('backup.dialog.exportWarning', '导出安全提示', 'text', 32, 350, 326, 72, 102),
  meta('backup.dialog.mergeHint', '合并恢复说明', 'text', 32, 360, 326, 72, 102),
  meta('backup.dialog.replaceWarning', '完整替换警告', 'text', 32, 360, 326, 72, 102),
  meta('backup.dialog.error', '加密备份错误', 'text', 32, 444, 326, 44, 102),
  meta('backup.dialog.progress', '加密备份进度', 'text', 32, 492, 326, 24, 102),
  meta('backup.dialog.cancel', '取消加密备份', 'button', 32, 540, 150, 44, 102),
  meta('backup.dialog.confirm', '确认加密备份', 'button', 208, 540, 150, 44, 102),
];

export const APPEARANCE_COMPONENTS: ComponentMetaInput[] = [
  meta('appearance.entry', '界面/外观', 'button', 282, 24, 92, 32, 8),
  meta('appearance.root', '外观设置面板', 'container', 0, 24, 390, 744, 80),
  meta('appearance.back', '返回设置', 'button', 8, 32, 44, 44, 82),
  meta('appearance.title', '界面/外观', 'text', 60, 38, 220, 32, 82),
  meta('appearance.scroll', '外观设置滚动区', 'list', 0, 80, 390, 688, 81),
  meta('appearance.catalog', '官方皮肤库', 'list', 0, 80, 390, 250, 82),
  meta('appearance.personal', '个人皮肤库', 'list', 0, 340, 390, 190, 82),
  meta('appearance.glassColor', '毛玻璃颜色', 'input', 16, 548, 358, 44, 82),
  meta('appearance.glassTransparency', '全局透明度', 'input', 16, 600, 358, 44, 82),
  meta('appearance.panelTransparency', '面板透明度', 'input', 16, 548, 358, 44, 82),
  meta('appearance.controlTransparency', '控件透明度', 'input', 16, 600, 358, 44, 82),
  meta('appearance.overlayTransparency', '弹层透明度', 'input', 16, 652, 358, 44, 82),
  meta('appearance.glassTintStrength', '毛玻璃主题着色强度', 'input', 16, 704, 358, 44, 82),
  meta('appearance.themeColor', '主题色', 'input', 16, 756, 170, 44, 82),
  meta('appearance.iconsFollowTheme', '图标跟随主题色', 'button', 206, 756, 168, 44, 82),
  meta('appearance.bubblePreview', '气泡样式实时预览', 'container', 16, 808, 358, 112, 82),
  meta('appearance.bubblePreview.user', '自己气泡预览', 'text', 150, 868, 210, 40, 83),
  meta('appearance.bubblePreview.assistant', '助手气泡预览', 'text', 30, 822, 220, 40, 83),
  meta('appearance.iconColor', '图标颜色', 'input', 16, 704, 170, 44, 82),
  meta('appearance.userBubble', '自己气泡颜色', 'input', 206, 704, 168, 44, 82),
  meta('appearance.assistantBubble', '助手气泡颜色', 'input', 16, 756, 170, 44, 82),
  meta('appearance.wallpaper', '自定义背景', 'button', 206, 756, 168, 44, 82),
  meta('appearance.savePersonal', '保存为个人皮肤', 'button', 16, 808, 170, 44, 82),
  meta('appearance.reset', '恢复皮肤默认', 'button', 206, 808, 168, 44, 82),
];

export const SETTINGS_ENTRY_COMPONENTS: ComponentMetaInput[] = [
  meta('entries.card', '功能入口卡片', 'container', 12, ENTRIES_TOP, 366, ENTRIES_HEIGHT, 1),
  meta('entries.title', '功能入口', 'text', 24, ENTRIES_TOP + 12, 342, 24),
  ...SETTINGS_ENTRY_LINKS.map((entry, index) =>
    meta('entries.' + entry.id, entry.label, 'button', 24, ENTRIES_TOP + 48 + index * 52, 342, 44),
  ),
];

const index = new Map(
  [...SETTINGS_PANEL_COMPONENTS, ...SETTINGS_BACKUP_COMPONENTS, ...SETTINGS_ENTRY_COMPONENTS, ...APPEARANCE_COMPONENTS].map(
    (entry) => [entry.id, entry],
  ),
);
export function settingsMeta(id: string): ComponentMetaInput {
  const found = index.get(id);
  if (!found) throw new Error('[settings] 未登记的部件：' + id);
  return found;
}
export const settingsEntryMeta = settingsMeta;
export const settingsBackupMeta = settingsMeta;
export function settingsBackupLayout(id: string): BoxLayout {
  return settingsMeta(id).layout;
}
export function settingsBackupComponentMeta(id: string): ComponentMeta {
  return buildComponentMeta(settingsMeta(id));
}
